<template>
  <span>
    <v-tooltip bottom>
      <template #activator="{ on, attrs }">
        <v-btn
          icon
          :color="color || 'primary'"
          :small="small"
          :disabled="disabled"
          v-bind="attrs"
          v-on="on"
          @click="open()"
        >
          <v-icon>mdi-qrcode-scan</v-icon>
        </v-btn>
      </template>
      <span>{{ label || 'Attach document via Varion Scanner' }}</span>
    </v-tooltip>

    <v-dialog v-model="dialog" width="440px" persistent>
      <v-card>
        <v-card-title class="text-h6">
          Varion Scanner
          <v-spacer></v-spacer>
          <v-btn icon @click="close()"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="text-center">
          <!-- creating -->
          <div v-if="status === 'creating'" class="py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-3 grey--text">Generating code…</div>
          </div>

          <!-- waiting for the phone -->
          <div v-else-if="status === 'waiting'">
            <p class="mb-2">Open the <b>Varion Scanner</b> app on your phone and scan this code.</p>
            <div class="qr-wrap mx-auto" v-html="qrSvg"></div>
            <div class="mt-3 d-flex align-center justify-center">
              <v-progress-circular indeterminate size="18" width="2" color="primary" class="mr-2"></v-progress-circular>
              <span class="grey--text">Waiting for the photo… {{ countdownLabel }}</span>
            </div>
          </div>

          <!-- completed -->
          <div v-else-if="status === 'completed'" class="py-6">
            <v-icon color="success" size="56">mdi-check-circle</v-icon>
            <div class="mt-2 text-h6">Document attached</div>
            <div v-if="doneMessage" class="grey--text mt-1">{{ doneMessage }}</div>
          </div>

          <!-- expired / error -->
          <div v-else-if="status === 'expired' || status === 'error'" class="py-6">
            <v-icon :color="status === 'expired' ? 'warning' : 'error'" size="48">
              {{ status === 'expired' ? 'mdi-timer-alert' : 'mdi-alert-circle' }}
            </v-icon>
            <div class="mt-2">{{ errorMsg || (status === 'expired' ? 'The code expired.' : 'Something went wrong.') }}</div>
            <v-btn color="primary" class="mt-4" small @click="open()">Generate a new code</v-btn>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="close()">{{ status === 'completed' ? 'Done' : 'Cancel' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import axios from 'axios'
import { io } from 'socket.io-client'
import { mapGetters } from 'vuex'

export default {
  name: 'VarionScannerButton',
  props: {
    // order | receivingReport | purchaseOrder | booklist | booklistNew
    targetType: { type: String, required: true },
    targetId: { type: [Number, String], default: null },
    label: String,
    color: String,
    small: Boolean,
    disabled: Boolean,
  },
  data: () => ({
    dialog: false,
    status: 'creating', // creating | waiting | completed | expired | error
    qrSvg: '',
    token: null,
    deepLink: null,
    expiresAt: null,
    errorMsg: '',
    doneMessage: '',
    socket: null,
    pollTimer: null,
    countdownTimer: null,
    secondsLeft: 0,
  }),
  computed: {
    ...mapGetters(['getEndpoint']),
    countdownLabel() {
      if (this.secondsLeft <= 0) return ''
      let m = Math.floor(this.secondsLeft / 60)
      let s = this.secondsLeft % 60
      return `(${m}:${String(s).padStart(2, '0')})`
    },
  },
  methods: {
    async open() {
      this.dialog = true
      this.status = 'creating'
      this.errorMsg = ''
      this.doneMessage = ''
      this.qrSvg = ''
      try {
        let { data } = await axios.post(`${this.getEndpoint}/api/scanSession`, {
          targetType: this.targetType,
          targetId: this.targetId,
        })
        if (!data.success) throw new Error('Could not create a scan session.')
        this.token = data.data.token
        this.deepLink = data.data.deepLink
        this.qrSvg = data.data.qrSvg || ''
        this.expiresAt = data.data.expiresAt
        this.status = 'waiting'
        this.connectSocket()
        this.startPolling()
        this.startCountdown()
      } catch (err) {
        this.status = 'error'
        this.errorMsg = this.extractError(err)
      }
    },
    connectSocket() {
      try {
        this.socket = io(this.getEndpoint)
        this.socket.on('connect', () => this.socket.emit('scanIdentify', this.token))
        this.socket.on('scanComplete', (payload) => this.handleComplete(payload))
      } catch (e) {
        // polling still covers completion if the socket can't connect
        console.log('Scan socket error:', e)
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(async () => {
        try {
          let { data } = await axios.get(`${this.getEndpoint}/api/scanSession/${this.token}/status`)
          if (!data.success) return
          if (data.data.status === 'completed') {
            this.handleComplete({ ...data.data.metadata, status: 'completed', targetType: data.data.targetType, targetId: data.data.targetId })
          } else if (data.data.status === 'expired') {
            this.status = 'expired'
            this.teardown()
          }
        } catch (e) { /* transient */ }
      }, 4000)
    },
    stopPolling() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null }
    },
    startCountdown() {
      this.stopCountdown()
      const tick = () => {
        this.secondsLeft = Math.max(0, Math.round((new Date(this.expiresAt) - new Date()) / 1000))
        if (this.secondsLeft <= 0 && this.status === 'waiting') {
          this.status = 'expired'
          this.teardown()
        }
      }
      tick()
      this.countdownTimer = setInterval(tick, 1000)
    },
    stopCountdown() {
      if (this.countdownTimer) { clearInterval(this.countdownTimer); this.countdownTimer = null }
    },
    handleComplete(payload) {
      if (this.status === 'completed') return
      this.status = 'completed'
      if (payload && payload.booklistId) {
        this.doneMessage = 'A new booklist was created from the photo.'
      } else {
        this.doneMessage = 'The photo was attached to this record.'
      }
      this.teardown()
      this.$emit('complete', payload)
    },
    teardown() {
      this.stopPolling()
      this.stopCountdown()
      if (this.socket) {
        try { this.socket.disconnect() } catch (e) { /* noop */ }
        this.socket = null
      }
    },
    async close() {
      // best-effort cancel so a walked-away code can't be used later
      if (this.status === 'waiting' && this.token) {
        try { await axios.post(`${this.getEndpoint}/api/scanSession/${this.token}/cancel`) } catch (e) { /* noop */ }
      }
      this.teardown()
      this.dialog = false
    },
    extractError(err) {
      let d = err && err.response && err.response.data
      if (typeof d === 'string') return d
      if (d && d.error && d.error.message) return d.error.message
      if (d && typeof d.error === 'string') return d.error
      return (err && err.message) || 'Something went wrong.'
    },
  },
  beforeDestroy() {
    this.teardown()
  },
}
</script>

<style scoped>
.qr-wrap {
  width: 240px;
  max-width: 80%;
  background: #fff;
  padding: 12px;
  border-radius: 8px;
}
.qr-wrap >>> svg { width: 100%; height: auto; display: block; }
</style>
